import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getAbcCurveReport } from '../../services/api';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../../utils/reportBranding';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import { toDateInputValue } from '../../utils/datetimeLocal';
import { useEstabelecimentoFiltroApi } from '../../context/EstabelecimentoContext';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const localDate = toDateInputValue;

const colorOf = (classe) =>
  classe === 'A' ? 'success' : classe === 'B' ? 'warning' : 'default';

export default function AbcCurveReport() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const [filters, setFilters] = useState({
    de: localDate(first),
    ate: localDate(now),
    criterio: 'valor',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAbcCurveReport({ ...filters, estabelecimentoId });
      setData(response.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível gerar a curva ABC.');
    } finally {
      setLoading(false);
    }
  }, [filters, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const abcAccessors = useMemo(() => ({
    quantidade: (x) => Number(x.quantidade) || 0,
    valor: (x) => Number(x.valor) || 0,
    percentual: (x) => Number(x.percentual) || 0,
    percentualAcumulado: (x) => Number(x.percentualAcumulado) || 0,
  }), []);
  const { sorted: abcOrdenados, orderBy, order, requestSort } = useClientSort(
    data?.items || [],
    'ranking',
    'asc',
    abcAccessors,
  );

  const exportPdf = async () => {
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) {
      setError('Libere pop-ups para gerar o PDF.');
      return;
    }
    try {
      const company = await loadReportCompany();
      const rows = (data?.items || [])
        .map(
          (x) => `<tr>
            <td>${html(x.ranking)}</td><td>${html(x.classe)}</td><td>${html(x.codigo)}</td>
            <td>${html(x.descricao)}</td>
            <td style="text-align:right">${html(Number(x.quantidade).toLocaleString('pt-BR'))}</td>
            <td style="text-align:right">${html(currency(x.valor))}</td>
            <td style="text-align:right">${html(Number(x.percentual).toFixed(2))}%</td>
            <td style="text-align:right">${html(Number(x.percentualAcumulado).toFixed(2))}%</td>
          </tr>`
        )
        .join('');
      popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Curva ABC</title>
        <style>
          body{font:10px Arial;padding:18px;color:#172033}
          h1{margin:4px 0 0}
          ${REPORT_BRAND_CSS}
          table{width:100%;border-collapse:collapse;margin-top:12px}
          th,td{border-bottom:1px solid #e2e6ed;padding:5px;text-align:left}
          th{background:#eaf6fe}
        </style></head><body>
        ${reportBrandHtml(company, { title: 'Curva ABC de produtos' })}
        <div style="margin-top:8px">Período: ${html(filters.de)} a ${html(filters.ate)} · Critério: ${html(filters.criterio)}</div>
        <div>Total: ${html(currency(data?.valorTotal))} · ${html(data?.produtos || 0)} produtos</div>
        <table><thead><tr>
          <th>#</th><th>Classe</th><th>Código</th><th>Produto</th><th>Qtd</th><th>Valor</th><th>%</th><th>% Acum.</th>
        </tr></thead><tbody>${rows}</tbody></table></body></html>`);
      popup.document.close();
      setTimeout(() => popup.print(), 300);
    } catch (e) {
      popup.close();
      setError(e?.response?.data?.error || 'Falha ao gerar PDF.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Curva ABC"
        subtitle="Classe A até 80% acumulado, B até 95% e C o restante (Pareto)."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.de}
            onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.ate}
            onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Critério"
            size="small"
            value={filters.criterio}
            onChange={(e) => setFilters((p) => ({ ...p, criterio: e.target.value }))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="valor">Valor</MenuItem>
            <MenuItem value="quantidade">Quantidade</MenuItem>
          </TextField>
          <Button variant="contained" onClick={load} disabled={loading}>Gerar</Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={!data} onClick={exportPdf}>
            PDF
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Produtos', data?.produtos || 0],
          ['Faturamento', currency(data?.valorTotal)],
          ['Classe A', `${data?.faixaA?.produtos || 0} · ${Number(data?.faixaA?.percentualValor || 0).toFixed(1)}%`],
          ['Classe B', `${data?.faixaB?.produtos || 0} · ${Number(data?.faixaB?.percentualValor || 0).toFixed(1)}%`],
          ['Classe C', `${data?.faixaC?.produtos || 0} · ${Number(data?.faixaC?.percentualValor || 0).toFixed(1)}%`],
        ].map(([label, value]) => (
          <Grid item xs={6} md={2.4} key={label}>
            <Card variant="outlined"><CardContent>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" fontWeight={900}>{value}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="ranking" label="#" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="classe" label="Classe" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="quantidade" label="Quantidade" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="percentual" label="%" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="percentualAcumulado" label="% Acum." align="right" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {abcOrdenados.map((x) => (
              <TableRow key={x.produtoId}>
                <TableCell data-label="#">{x.ranking}</TableCell>
                <TableCell data-label="Classe"><Chip size="small" color={colorOf(x.classe)} label={x.classe} /></TableCell>
                <TableCell data-label="Código">{x.codigo}</TableCell>
                <TableCell data-label="Produto">{x.descricao}</TableCell>
                <TableCell align="right" data-label="Quantidade">{Number(x.quantidade).toLocaleString('pt-BR')}</TableCell>
                <TableCell align="right" data-label="Valor"><strong>{currency(x.valor)}</strong></TableCell>
                <TableCell align="right" data-label="%">{Number(x.percentual).toFixed(2)}%</TableCell>
                <TableCell align="right" data-label="% acum.">{Number(x.percentualAcumulado).toFixed(2)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </Box>
  );
}
